import React from 'react';
import { ROUTES } from '../../constants/constants';
import KpiCard from './KpiCard';
import StatRow from './StatRow';
import Button from '../common/Button';

function DashboardTab({ stats, users, loading, navigate }) {
  const userStats = stats || {};
  const totalUsers = userStats.totalUsers || 0;

  if (loading) {
    return (
      <div className="text-center py-20 text-[#7a7060]">
        <div className="animate-pulse">Loading dashboard data…</div>
      </div>
    );
  }

  return (
    <div>
      {/* KPI Cards - 8 cards in 2 rows */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard label="Total Users" value={userStats.totalUsers || 0} color="#145f5f" icon="&#128101;" />
        <KpiCard label="Active" value={userStats.activeUsers || 0} color="#4a6b3f" icon="&#9989;" />
        <KpiCard label="Professionals" value={userStats.professionalUsers || 0} color="#3a4a66" icon="&#11088;" />
        <KpiCard label="Corporate" value={userStats.corporateUsers || 0} color="#6f6049" icon="&#128200;" />
        <KpiCard label="Admins" value={userStats.adminUsers || 0} color="#b8973a" icon="&#128081;" />
        <KpiCard label="Verified" value={userStats.verifiedUsers || 0} color="#4a6b3f" icon="&#128272;" />
        <KpiCard label="Services" value={userStats.totalServices || 0} color="#b07a1a" icon="&#9881;" />
        <KpiCard label="AI Models" value={userStats.totalAiModels || 0} color="#b8973a" icon="&#129302;" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Users by Role */}
        <div className="bg-white rounded-[12px] border p-4 transition-shadow hover:shadow-sm" style={{ borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,.07)' }}>
          <h3 className="text-[13px] font-bold text-[#08111f] mb-3 pb-2 border-b" style={{ borderColor: '#ddd6c6' }}>Users by Role</h3>
          {(userStats.usersByRole || []).length === 0 ? (
            <div className="text-[12px] text-[#7a7060]">No role data available</div>
          ) : (
            (userStats.usersByRole || []).map((r) => (
              <StatRow key={r._id || 'unknown'} label={String(r._id || 'Unknown').replace(/_/g, ' ')} value={r.count} total={totalUsers} />
            ))
          )}
        </div>

        {/* Users by Profession */}
        <div className="bg-white rounded-[12px] border p-4 transition-shadow hover:shadow-sm" style={{ borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,.07)' }}>
          <h3 className="text-[13px] font-bold text-[#08111f] mb-3 pb-2 border-b" style={{ borderColor: '#ddd6c6' }}>Users by Profession</h3>
          {(userStats.usersByProfession || []).length === 0 ? (
            <div className="text-[12px] text-[#7a7060]">No profession data available</div>
          ) : (
            (userStats.usersByProfession || []).map((p) => (
              <StatRow key={p._id || 'unknown'} label={String(p._id || 'Unknown')} value={p.count} total={totalUsers} />
            ))
          )}
        </div>
      </div>

      {/* Recent Users Table */}
      <div className="bg-white rounded-[12px] border overflow-hidden" style={{ borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,.07)' }}>
        <div className="flex items-center justify-between px-4 py-3" style={{ background: 'linear-gradient(135deg, #f8f5ef 0%, #ffffff 100%)' }}>
          <h3 className="text-[13px] font-bold text-[#08111f]">Recent Registrations</h3>
          <Button variant="outline" size="sm" rightIcon={<span aria-hidden>&rarr;</span>} onClick={() => navigate(ROUTES.ADMIN_USERS)}>
            View all
          </Button>
        </div>
        <div className="overflow-x-auto cs-table-scroll">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#7a7060] border-b" style={{ borderColor: '#e8e2d6', background: '#f8f5ef' }}>
                <th className="text-left py-2 px-4" style={{ width: '18%' }}>Name</th>
                <th className="text-left py-2 px-4" style={{ width: '30%' }}>Email</th>
                <th className="text-left py-2 px-4" style={{ width: '16%' }}>Mobile</th>
                <th className="text-left py-2 px-4" style={{ width: '18%' }}>Role</th>
                <th className="text-left py-2 px-4" style={{ width: '18%' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u._id || u.id} className="border-b last:border-0 transition-colors hover:bg-[#f8f5ef]" style={{ borderColor: '#ddd6c6' }}>
                  <td className="py-2.5 px-4 font-medium text-[#08111f]">{u.fullName}</td>
                  <td className="py-2.5 px-4 text-[#7a7060]">{u.email}</td>
                  <td className="py-2.5 px-4 text-[#7a7060]">{u.mobile}</td>
                  <td className="py-2.5 px-4">
                    <span className="inline-flex whitespace-nowrap px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: '#145f5f22', color: '#145f5f' }}>
                      {u.roleName || u.role || 'user'}
                    </span>
                  </td>
                  <td className="py-2.5 px-4">
                    <span
                      className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                      style={u.isActive !== false ? { background: '#e8efe5', color: '#4a6b3f' } : { background: '#f2e9e6', color: '#9a564a' }}
                    >
                      {u.isActive !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-[#7a7060]">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default DashboardTab;
